/**
 * Decides whether Ryn Replay capture should pause because the global DOM
 * mutation rate has spiked into a storm, and when it has settled enough to
 * resume. This is the centralized, page-agnostic successor to the per-page
 * focus-pause guard (#31398): instead of a hand-maintained selector list naming
 * the one grid known to freeze, it watches the actual mutation rate and
 * self-protects on ANY heavy page in ANY replay-enabled app.
 *
 * The freeze is the rrweb recorder serializing a heavy per-keystroke DOM
 * mutation storm on the browser main thread. On stg the Equipment Usage grid
 * measured a median of 532 mutations per 300ms window during rapid keying
 * (p90 966, peak 4308), blocking the main thread ~3s across 25 long-tasks.
 * Angular + Kendo first paint also spikes well above 300 (Sparta boot and a
 * five-row Equipment Dispatch load both tripped a same-tick pause). Pausing on
 * the first window that crosses `burstThreshold` therefore false-pauses every
 * visit. `sustainMs` requires the rate to STAY at or above the threshold before
 * pausing: a first-paint spike ages out of the 300ms window and never pauses; a
 * multi-second keying storm still does.
 *
 * Pure on purpose, exactly like `isFocusWithinHeavyElement`: the service feeds
 * it a cheap mutation COUNT per tick (never a serialized batch) plus periodic
 * count-0 heartbeats so it can notice a storm has stopped, and acts on the
 * returned decision. Keeping the decision here means the storm/settle logic is
 * unit-tested without a live recorder, an rrweb backend, or a real DOM.
 */
export type MutationBurstAction = 'pause' | 'resume' | null;

export interface MutationBurstConfig {
  /** Sliding window, in ms, whose summed mutation count is the "rate". */
  windowMs: number;
  /** Pause once the windowed sum reaches this many mutations. */
  burstThreshold: number;
  /** The window is considered "quiet" once its sum is at or below this. */
  settleThreshold: number;
  /** Resume only after the window has stayed quiet for this many ms. */
  settleMs: number;
  /**
   * Pause only after the windowed sum has stayed at or above `burstThreshold`
   * for this many ms. `0` pauses on the first crossing (test-friendly).
   */
  sustainMs: number;
  /**
   * Pause IMMEDIATELY, bypassing `sustainMs`, once a single windowed sum reaches
   * this many mutations. A window this large is never a benign first paint (the
   * measured first-paint peak is ~4308): it is a catastrophic render/snapshot
   * storm that freezes the tab within the window itself, so waiting `sustainMs`
   * to confirm it is "sustained" only lets the freeze land first. Set ABOVE the
   * benign first-paint peak so ordinary boots never trip it. `0` disables the
   * immediate path, leaving `sustainMs` as the only pause trigger.
   */
  hardCeiling: number;
  /**
   * Minimum time to stay paused once paused, before ANY resume, on top of the
   * `settleMs` quiet requirement. During active editing the mutation rate
   * oscillates: a burst pauses, the rate drops for `settleMs`, capture resumes
   * and re-emits a full DOM snapshot of the heavy grid, then the next keystroke
   * storms and pauses again. On a large grid each resume-snapshot is expensive,
   * so rapid pause/resume cycling is its own cost (measured live on Equipment
   * Usage editing, #31398). Holding the pause for at least `minPauseMs`
   * collapses a burst of edit-session storms into a single pause with one
   * resume-snapshot instead of many. `0` disables the floor, leaving `settleMs`
   * as the only resume gate.
   */
  minPauseMs: number;
}

/**
 * Per-app overrides accepted at `environment.rynReplay.mutationBurstPause`. Every
 * threshold falls back to {@link DEFAULT_MUTATION_BURST_CONFIG}; `enabled: false`
 * is the kill switch that skips the guard entirely. Shared by the recorder
 * service and {@link startMutationBurstWatch} so the shape lives in one place.
 */
export interface MutationBurstPauseOptions extends Partial<MutationBurstConfig> {
  enabled?: boolean;
}

/**
 * Defaults from the stg Equipment Usage benchmark (2026-09-04) plus the
 * Sparta boot / Equipment Dispatch false-pauses (2026-09-07). `burstThreshold`
 * 300 still sits below the keying-storm median so a real freeze is caught;
 * `sustainMs` 1000 skips a one-window first-paint spike; settle still waits
 * ~0.8s of near-idle before resume() re-snapshots. `hardCeiling` 8000 sits well
 * above the measured first-paint peak (4308) and far below a catastrophic single
 * window (the Equipment Usage freeze measured 62,644 in one 300ms window), so it
 * catches a tab-freezing storm on its first tick without false-pausing on boot.
 * Overridable per app via `environment.rynReplay`.
 */
export const DEFAULT_MUTATION_BURST_CONFIG: MutationBurstConfig = {
  windowMs: 300,
  burstThreshold: 300,
  settleThreshold: 12,
  settleMs: 800,
  sustainMs: 1000,
  hardCeiling: 8000,
  minPauseMs: 2500,
};

export interface MutationBurstDecision {
  /** 'pause' or 'resume' on a state transition, otherwise null (no change). */
  action: MutationBurstAction;
  /** The windowed mutation sum at this tick, for the visible replay marker. */
  windowSum: number;
}

/**
 * Stateful sliding-window detector. One instance per recorder. Every input is a
 * `feed(nowMs, count)`: a real mutation batch (`count > 0`) or a heartbeat
 * (`count === 0`) whose only job is to advance time so old samples expire and a
 * finished storm is detected. `nowMs` is injected (not read from a clock) so the
 * whole state machine is deterministic under test.
 */
export class MutationBurstDetector {
  private readonly samples: { at: number; count: number }[] = [];
  private paused = false;
  /** When the current pause began, or null while recording. Gates `minPauseMs`. */
  private pausedAt: number | null = null;
  /** When the current quiet stretch began while paused, or null if not quiet. */
  private settleStartedAt: number | null = null;
  /** When the current above-threshold stretch began while recording, or null. */
  private burstStartedAt: number | null = null;

  constructor(
    private readonly config: MutationBurstConfig = DEFAULT_MUTATION_BURST_CONFIG
  ) {}

  /** True while the detector is holding capture paused. */
  get isPaused(): boolean {
    return this.paused;
  }

  feed(nowMs: number, count: number): MutationBurstDecision {
    // Drop samples that have aged out of the window. A sample exactly windowMs
    // old is out, matching "within a 300ms window".
    const cutoff = nowMs - this.config.windowMs;
    while (this.samples.length > 0 && this.samples[0].at <= cutoff) {
      this.samples.shift();
    }
    // Only real batches carry weight; heartbeats exist purely to prune + re-check.
    if (count > 0) {
      this.samples.push({ at: nowMs, count });
    }
    const windowSum = this.samples.reduce((sum, s) => sum + s.count, 0);

    if (!this.paused) {
      // A single catastrophic window freezes the tab before `sustainMs` could
      // ever confirm the storm is sustained, so it bypasses that clock entirely.
      if (this.config.hardCeiling > 0 && windowSum >= this.config.hardCeiling) {
        this.paused = true;
        this.pausedAt = nowMs;
        this.burstStartedAt = null;
        this.settleStartedAt = null;
        return { action: 'pause', windowSum };
      }
      if (windowSum >= this.config.burstThreshold) {
        if (this.burstStartedAt === null) {
          this.burstStartedAt = nowMs;
        }
        if (nowMs - this.burstStartedAt >= this.config.sustainMs) {
          this.paused = true;
          this.pausedAt = nowMs;
          this.burstStartedAt = null;
          this.settleStartedAt = null;
          return { action: 'pause', windowSum };
        }
      } else {
        this.burstStartedAt = null;
      }
      return { action: null, windowSum };
    }

    // Paused: resume only after a sustained quiet stretch AND, on top of that, a
    // minimum time spent paused. The quiet requirement (`settleMs`) proves the
    // storm ended; the `minPauseMs` floor keeps an editing session that storms
    // in bursts from cycling pause -> resume -> pause and re-snapshotting the
    // heavy grid on every resume (#31398 follow-up).
    if (windowSum <= this.config.settleThreshold) {
      if (this.settleStartedAt === null) {
        this.settleStartedAt = nowMs;
      } else if (
        nowMs - this.settleStartedAt >= this.config.settleMs &&
        (this.config.minPauseMs <= 0 ||
          this.pausedAt === null ||
          nowMs - this.pausedAt >= this.config.minPauseMs)
      ) {
        this.paused = false;
        this.pausedAt = null;
        this.settleStartedAt = null;
        return { action: 'resume', windowSum };
      }
    } else if (count > 0) {
      // Still stormy AND a fresh batch just landed: restart the settle clock.
      // A count-0 heartbeat must NOT reset it. windowSum can only rise above
      // settleThreshold via a new batch (pruning only lowers it), so gating the
      // reset on count > 0 keeps settle timing independent of where heartbeats
      // happen to fall relative to a lingering storm sample.
      this.settleStartedAt = null;
    }
    return { action: null, windowSum };
  }
}

/**
 * Wire a detector to the live DOM: a count-only MutationObserver feeds it the
 * mutation total each tick, plus count-0 heartbeats so a finished storm is
 * detected. `onDecision` fires only on a pause/resume transition, with the
 * resolved config so the caller can stamp the threshold onto its marker. Returns
 * a teardown (disconnect + clear). No-op (and a no-op teardown) with no document
 * or MutationObserver (SSR / non-browser). This is the glue half of the guard;
 * the decision logic it drives lives in {@link MutationBurstDetector}.
 */
export function startMutationBurstWatch(
  overrides: Partial<MutationBurstConfig> | undefined,
  onDecision: (
    decision: MutationBurstDecision,
    config: MutationBurstConfig
  ) => void
): () => void {
  if (
    typeof document === 'undefined' ||
    typeof MutationObserver === 'undefined'
  ) {
    return () => undefined;
  }
  const config: MutationBurstConfig = {
    windowMs: overrides?.windowMs ?? DEFAULT_MUTATION_BURST_CONFIG.windowMs,
    burstThreshold:
      overrides?.burstThreshold ?? DEFAULT_MUTATION_BURST_CONFIG.burstThreshold,
    settleThreshold:
      overrides?.settleThreshold ??
      DEFAULT_MUTATION_BURST_CONFIG.settleThreshold,
    settleMs: overrides?.settleMs ?? DEFAULT_MUTATION_BURST_CONFIG.settleMs,
    sustainMs: overrides?.sustainMs ?? DEFAULT_MUTATION_BURST_CONFIG.sustainMs,
    hardCeiling:
      overrides?.hardCeiling ?? DEFAULT_MUTATION_BURST_CONFIG.hardCeiling,
    minPauseMs:
      overrides?.minPauseMs ?? DEFAULT_MUTATION_BURST_CONFIG.minPauseMs,
  };
  const detector = new MutationBurstDetector(config);
  let pending = 0;
  // One tick: feed the batches seen since the last tick, clear the accumulator,
  // and route any pause/resume transition to the caller. Shared by the
  // setInterval below and the synchronous hard-ceiling path in the observer, so
  // both drive the decision through the same detector authority.
  const runTick = () => {
    const decision = detector.feed(Date.now(), pending);
    pending = 0;
    if (decision.action) {
      onDecision(decision, config);
    }
  };
  const observer = new MutationObserver((records) => {
    // COUNT only. Never read record contents: serializing here would reproduce
    // the very main-thread cost this guard exists to sidestep.
    pending += records.length;
    // #33939: a single catastrophic batch is serialized by rrweb's OWN
    // MutationObserver synchronously in this same microtask, before the
    // setInterval below can run, so a timer-driven hard-ceiling check cannot
    // prevent the first-batch freeze. Decide it HERE instead. The recorder
    // constructs this observer before rrweb's (rrweb loads via a lazy async
    // import in the backend, so its record() runs a microtask later) and
    // MutationObservers fire in construction order, so pausing on this batch
    // disconnects rrweb before its callback runs and the offending batch is
    // never serialized. `0` disables the ceiling (same as the detector).
    if (
      config.hardCeiling > 0 &&
      !detector.isPaused &&
      pending >= config.hardCeiling
    ) {
      runTick();
    }
  });
  observer.observe(document, {
    subtree: true,
    childList: true,
    attributes: true,
    characterData: true,
  });
  // Tick faster than the window so a storm is caught mid-window and count-0
  // heartbeats let the detector notice a storm has ended. The recorder starts
  // this watch outside NgZone: a zoned 100ms interval also runs change
  // detection, which is what Chrome reports as a stream of setTimeout
  // violations even though this callback only adds integers.
  const handle = setInterval(runTick, Math.min(config.windowMs, 100));
  return () => {
    clearInterval(handle);
    observer.disconnect();
  };
}
